import PaymentRetention from "./PaymentRetention";
import React, { useCallback, useEffect, useState } from "react";
import { useI18n } from "@/contexts/LanguageContext";
import { api, extractError } from "@/lib/api";
import { toast } from "sonner";

const nextDay = value => { const d = new Date(`${value}T12:00:00Z`); d.setUTCDate(d.getUTCDate() + 1); return d.toISOString().slice(0, 10); };

const previousDay = value => { const d = new Date(`${value}T12:00:00Z`); d.setUTCDate(d.getUTCDate() - 1); return d.toISOString().slice(0, 10); };

const PHASES = ["free_launch", "promotional", "standard"];

export default function AdminBilling() {
  const { t } = useI18n();
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [phase, setPhase] = useState("free_launch");
  const [endsAt, setEndsAt] = useState("");
  const [promoStart, setPromoStart] = useState("");
  const [promoEnd, setPromoEnd] = useState("");
  const [autoStandard, setAutoStandard] = useState(false);
  const [autoAdvance, setAutoAdvance] = useState(true);
  const [currency, setCurrency] = useState("eur");
  const [prices, setPrices] = useState({
    promotional: { monthly: 990, six_month: 4990, yearly: 8990 },
    standard: { monthly: 1590, six_month: 7990, yearly: 12990 },
  });

  const load = useCallback(async () => {
    try {
      const r = await api.get("/admin/billing-config");
      setCfg(r.data);
      setPhase(r.data.phase || "free_launch");
      setEndsAt(r.data.free_launch_last_day || r.data.free_launch_ends_at?.slice(0, 10) || "");
      setPromoStart(r.data.promotional_starts_on || "");
      setPromoEnd(r.data.promotional_ends_on || "");
      setAutoStandard(!!r.data.auto_advance_after_promotional);
      setAutoAdvance(!!r.data.auto_advance_after_free_launch);
      setCurrency(r.data.currency || "eur");
      if (r.data.prices_cents) {
        setPrices(previous => ({
          promotional: { ...previous.promotional, ...(r.data.prices_cents.promotional || {}) },
          standard: { ...previous.standard, ...(r.data.prices_cents.standard || {}) },
        }));
      }
    } catch (e) {
      setError(extractError(e));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    const start = promoStart || (endsAt && autoAdvance ? nextDay(endsAt) : "");
    if ((promoEnd && (!start || promoEnd < start)) || (autoStandard && (!start || !promoEnd))) {
      toast.error(t("app.admin.billing.invalidCalendar"));
      return;
    }
    setSaving(true);
    try {
      const body = {
        phase,
        free_launch_last_day: endsAt || null,
        promotional_starts_on: promoStart || (endsAt && autoAdvance ? nextDay(endsAt) : null),
        promotional_ends_on: promoEnd || null,
        auto_advance_after_promotional: autoStandard,
        auto_advance_after_free_launch: autoAdvance,
        currency,
        prices_cents: prices,
      };
      await api.patch("/admin/billing-config", body);
      toast.success(t("app.admin.billing.saved"));
      await load();
    } catch (e) {
      toast.error(extractError(e, t("app.admin.billing.error")));
    } finally {
      setSaving(false);
    }
  };

  const updatePrice = (tier, plan, value) => {
    const int = parseInt(value, 10);
    setPrices((p) => ({
      ...p,
      [tier]: {
        ...p[tier],
        [plan]: Number.isFinite(int) && int >= 0 ? int : 0,
      },
    }));
  };

  return (
    <div className="max-w-4xl" data-testid="admin-billing-page">
      <p className="nvl-overline" style={{ color: "hsl(var(--nvl-accent))" }}>
        {t("app.admin.nav.billing")}
      </p>
      <h1 className="mt-2 text-3xl md:text-4xl">{t("app.admin.billing.title")}</h1>
      <p className="mt-2 text-sm" style={{ color: "hsl(var(--nvl-ink-soft))" }}>
        {t("app.admin.billing.subtitle")}
      </p>

      {error ? (
        <div className="mt-6 text-sm" style={{ color: "hsl(var(--destructive))" }}>
          {error}
        </div>
      ) : null}

      <div className="mt-8 nvl-card p-6 space-y-6" data-testid="admin-billing-form">
        <div>
          <label className="text-xs uppercase tracking-widest"
                 style={{ color: "hsl(var(--nvl-ink-soft))" }}>
            {t("app.admin.billing.phaseLabel")}
          </label>
          <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-3">
            {PHASES.map((p) => {
              const active = phase === p;
              return (
                <button
                  key={p}
                  type="button"
                  onClick={() => setPhase(p)}
                  data-testid={`admin-billing-phase-${p}`}
                  className="px-4 py-3 rounded-md border text-sm text-left"
                  style={{
                    borderColor: active ? "hsl(var(--nvl-accent))" : "hsl(var(--nvl-border))",
                    backgroundColor: active ? "hsl(var(--nvl-accent) / 0.08)" : "transparent",
                  }}
                >
                  <div className="font-medium">
                    {t(`app.admin.billing.phase.${p}`)}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        <p className="text-sm">{t("app.admin.billing.launchStartHelp")}</p>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="text-xs uppercase tracking-widest"
                   style={{ color: "hsl(var(--nvl-ink-soft))" }}>
              {t("app.admin.billing.freeLaunchEndsAt")}
            </label>
            <input
              type="date"
              value={endsAt}
              onChange={(e) => { setEndsAt(e.target.value); if (e.target.value) setPromoStart(nextDay(e.target.value)); }}
              className="nvl-input mt-2"
              data-testid="admin-billing-ends-at"
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest"
                   style={{ color: "hsl(var(--nvl-ink-soft))" }}>
              {t("app.admin.billing.currency")}
            </label>
            <select
              value={currency}
              onChange={(e) => setCurrency(e.target.value)}
              className="nvl-input mt-2"
              data-testid="admin-billing-currency"
            >
              <option value="eur">EUR</option>
              <option value="usd">USD</option>
              <option value="gbp">GBP</option>
            </select>
          </div>
        </div>

        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={autoAdvance}
            onChange={(e) => setAutoAdvance(e.target.checked)}
            data-testid="admin-billing-auto-advance"
          />
          {t("app.admin.billing.autoAdvance")}
        </label>

        <section className="space-y-3" aria-label={t("app.admin.billing.promoDates")}>
          <h2 className="font-medium">{t("app.admin.billing.promoDates")}</h2>
          <p className="text-sm">{t("app.admin.billing.calendarHelp")}</p>
          <div className="grid md:grid-cols-2 gap-4">
            <label>{t("app.admin.billing.promoStart")}<input type="date" value={promoStart} onChange={e => { setPromoStart(e.target.value); if (e.target.value) setEndsAt(previousDay(e.target.value)); }} className="nvl-input mt-2" data-testid="admin-billing-promo-start" /></label>
            <label>{t("app.admin.billing.promoEnd")}<input type="date" min={promoStart || undefined} value={promoEnd} onChange={e => setPromoEnd(e.target.value)} className="nvl-input mt-2" data-testid="admin-billing-promo-end" /></label>
          </div>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={autoStandard} onChange={e => setAutoStandard(e.target.checked)} data-testid="admin-billing-auto-standard" />{t("app.admin.billing.autoStandard")}</label>
          {promoEnd && autoStandard && <p role="status">{t("app.admin.billing.standardFrom")}: {nextDay(promoEnd)} · 00:00 Europe/Rome</p>}
          <p className="text-sm">{t("app.admin.billing.noAutomaticCharge")}</p>
        </section>

        <div>
          <div className="nvl-overline mb-3" style={{ color: "hsl(var(--nvl-ink-soft))" }}>
            {t("app.admin.billing.pricesTitle")}
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            {["promotional", "standard"].map((tier) => (
              <div key={tier} className="border rounded-md p-4"
                   style={{ borderColor: "hsl(var(--nvl-border))" }}
                   data-testid={`admin-billing-tier-${tier}`}>
                <div className="font-medium mb-3">
                  {t(`app.admin.billing.${tier}`)}
                </div>
                {["monthly", "six_month", "yearly"].map((plan) => (
                  <div key={plan} className="flex items-center gap-3 mb-2">
                    <div className="w-24 text-sm"
                         style={{ color: "hsl(var(--nvl-ink-soft))" }}>
                      {t(`app.admin.billing.${plan === "six_month" ? "sixMonth" : plan}`)}
                    </div>
                    <input
                      type="number"
                      min="0"
                      value={prices[tier][plan] ?? 0}
                      onChange={(e) => updatePrice(tier, plan, e.target.value)}
                      className="nvl-input flex-1"
                      data-testid={`admin-billing-price-${tier}-${plan}`}
                    />
                    <div className="text-xs w-16 text-right"
                         style={{ color: "hsl(var(--nvl-ink-soft))" }}>
                      {((prices[tier][plan] ?? 0) / 100).toFixed(2)}
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>

        <div className="flex justify-end">
          <button
            onClick={save}
            disabled={saving}
            className="nvl-btn nvl-btn-primary disabled:opacity-50"
            data-testid="admin-billing-save"
          >
            {saving ? t("common.loading") : t("app.admin.billing.save")}
          </button>
        </div>
      </div>
      <PaymentRetention />
    </div>
  );
}
